import { Suspense } from 'react';

import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { Text } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';

import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';

export const RCFAvailableFunds = ({ accountId }: { accountId: string }) => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <div
                        className="space-y-s"
                        data-testid="rcf-available-funds-loading-skeleton"
                    >
                        <Skeleton className="h-m w-[100px]" />
                        <Skeleton className="h-xl w-full max-w-[300px]" />
                        <Skeleton className="h-m w-full" />
                        <Skeleton className="h-m w-[100px]" />
                    </div>
                }
            >
                <AvailableFunds accountId={accountId} />
            </Suspense>
        </ErrorBoundary>
    );
};

const AvailableFunds = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();

    const { data: account } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: ({ data }) =>
            data.accounts.find((account) => account.account_id === accountId),
        suspense: true
    });

    if (!account) {
        const errorMessage = `<RCFAvailableFunds /> - No account found with id: ${accountId}`;

        logError(errorMessage, {
            tags: TAGS.DE_LENDING
        });
        throw new Error(errorMessage);
    }

    const formalOfferAccessPeriod = account.formal_offer_access_period;

    if (!formalOfferAccessPeriod) {
        const errorMessage = `<RCFAvailableFunds /> - No formal offer access period found found for account: ${accountId}`;

        logError(errorMessage, {
            tags: TAGS.DE_LENDING
        });
        throw new Error(errorMessage);
    }

    const percentageOfFundsRemaining =
        (formalOfferAccessPeriod.available_cash /
            formalOfferAccessPeriod.max_amount) *
        100;

    const outstandingBalance = account.pending_balance!.total!;

    return (
        <div className="space-y-s" data-testid="rcf-available-funds">
            <div>
                <Text className="my-0 text-s font-reg">
                    <FormattedMessage
                        id="_.outstanding_view.rcf_available_funds"
                        defaultMessage="Available funds"
                    />
                </Text>
                <div className="flex flex-row items-end gap-xs">
                    <Text className="my-0 text-3xl">
                        <FormattedCurrency
                            value={formalOfferAccessPeriod.available_cash}
                        />
                    </Text>
                    <Text className="my-0 mb-2xs text-l">
                        /{' '}
                        <FormattedCurrency
                            value={formalOfferAccessPeriod.max_amount}
                        />
                    </Text>
                </div>
            </div>
            <div className="relative rounded-m border border-structure-80 bg-structure-100 p-s">
                <div
                    className="absolute left-0 top-0 block h-full rounded-m bg-error bg-gradient-to-r from-primary-40 to-primary-10"
                    style={{ width: `${percentageOfFundsRemaining}%` }}
                    data-testid="loan-progress-bar"
                />
            </div>
            <p
                className="my-0 text-s font-reg"
                data-testid="outstanding-balance"
            >
                <FormattedMessage
                    id="_.outstanding_view.rcf_outstanding_loan_amount"
                    defaultMessage="Outstanding balance: {outstandingBalance}"
                    values={{
                        outstandingBalance: (
                            <FormattedCurrency value={outstandingBalance} />
                        )
                    }}
                />
            </p>
        </div>
    );
};
